/**
 * The single legitimate boundary for `process.env` access (architecture §15.2).
 *
 * Servers call `loadApiEnv()` once at startup, fail fast on validation errors,
 * and pass the typed `ApiEnv` object down through dependency injection.
 *
 * The web app uses Vite's import.meta.env; `loadWebEnv()` validates the
 * Vite-exposed subset.
 */
import { apiEnvSchema, webEnvSchema, storeEnvSchema, EnvValidationError } from "./schema.js";
import type { ApiEnv, WebEnv, StoreEnv } from "./schema.js";

export type { ApiEnv, WebEnv, StoreEnv };
export { EnvValidationError };

/**
 * Loads and validates the API environment. Reads from `process.env` exactly once.
 * In production this `process.env` is itself populated by the Vault Agent
 * sidecar from a tmpfs-backed file (system-decisions §6).
 */
export function loadApiEnv(source: NodeJS.ProcessEnv = process.env): ApiEnv {
  const parsed = apiEnvSchema.safeParse(source);
  if (!parsed.success) {
    throw new EnvValidationError(parsed.error.issues);
  }
  return parsed.data;
}

/**
 * Validates the Vite-exposed env subset. Called by `apps/web` at module init.
 * `import.meta.env` is the only Vite-blessed source.
 */
export function loadWebEnv(source: Record<string, string | undefined>): WebEnv {
  const parsed = webEnvSchema.safeParse(source);
  if (!parsed.success) {
    throw new EnvValidationError(parsed.error.issues);
  }
  return parsed.data;
}

/**
 * Validates the store SSR server env. Called by `apps/store` at Node startup.
 */
export function loadStoreEnv(source: NodeJS.ProcessEnv = process.env): StoreEnv {
  const parsed = storeEnvSchema.safeParse(source);
  if (!parsed.success) {
    throw new EnvValidationError(parsed.error.issues);
  }
  return parsed.data;
}

/** Pretty-format env validation errors for startup logs. */
export function formatEnvError(error: EnvValidationError): string {
  const lines = error.issues.map((issue) => {
    const path = issue.path.join(".");
    return `  • ${path}: ${issue.message}`;
  });
  return ["Environment validation failed:", ...lines].join("\n");
}
